'use client';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PERMISSIONS } from '@repo/shared/domain';
import { getVerifyPermission } from '@repo/shared/helpers';
import DocumentTab from 'src/parts/tabs/DocumentTab';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import { getGroupDocuments } from 'src/api/personal/groupDetail';
import { getGroupDocumentTypes } from 'src/api/admin/documentTypes';

export const Route = createFileRoute('/personal_/groups/$groupId/documents/')({
	validateSearch: (search: Record<string, unknown>) => ({
		filter: (search.filter as string) || undefined,
		page: (search.page as number) || undefined,
	}),
	loaderDeps: ({ search }) => search,
	loader: async ({ params, deps }) => {
		const [documents, documentTypes] = await Promise.all([
			getGroupDocuments(params.groupId, { filter: deps.filter, page: deps.page }),
			getGroupDocumentTypes(),
		]);
		return { documents, documentTypes };
	},
	component: DocumentsPage,
});

function DocumentsPage() {
	const { groupId } = Route.useParams();
	const searchParams = Route.useSearch();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { documents: initialDocuments, documentTypes: initialDocumentTypes } = Route.useLoaderData();
	const { user: authenticatedUser } = useAuthenticatedUser();

	const verifyPermission = getVerifyPermission(authenticatedUser?.permissions || []);

	const { data: documents, refetch: refetchDocuments } = useQuery({
		queryKey: ['personal-group-documents', groupId, searchParams],
		queryFn: () => getGroupDocuments(groupId, { filter: searchParams.filter, page: searchParams.page }),
		initialData: initialDocuments,
	});

	const { data: documentTypes } = useQuery({
		queryKey: ['group-document-types'],
		queryFn: () => getGroupDocumentTypes(),
		initialData: initialDocumentTypes,
	});

	const revalidateGroupDetail = () => {
		refetchDocuments();
		queryClient.invalidateQueries({ queryKey: ['personal-group', groupId] });
	};

	const handleReload = () => {
		refetchDocuments();
	};

	return (
		<DocumentTab
			data={documents}
			documentTypes={documentTypes?.data || []}
			revalidateDetail={revalidateGroupDetail}
			apiUrl={`/api/personal-dashboard/groups/${groupId}/documents`}
			onReload={handleReload}
			onSelectDocument={(id: string) =>
				navigate({
					to: '/personal/groups/$groupId/documents/$documentId',
					params: { groupId, documentId: id },
				})
			}
			permissions={{
				metadata: verifyPermission([PERMISSIONS.personalGroupsDocuments]),
				fullView: verifyPermission([PERMISSIONS.personalGroupsDocuments]),
				upload: verifyPermission([PERMISSIONS.personalGroupsDocuments]),
				archive: verifyPermission([PERMISSIONS.personalGroupsDocuments]),
				edit: verifyPermission([PERMISSIONS.personalGroupsDocuments]),
			}}
		/>
	);
}
